import React, { useCallback, useEffect, useState } from "react";
import ComponentCard from "../../components/common/ComponentCard";
import FlashcardTable from "../../components/tables/FlashcardTable";
import PageMeta from "../../components/common/PageMeta";
import FlashcardService from "../../services/flashcard.service";
import Input from "../../components/form/input/InputField";
import { Search } from "lucide-react";
import { useParams, useSearchParams } from "react-router";
import ImportFlashcardsModal from "../Flashcard/ImportFlashcardsModal";
import { useTranslation } from "react-i18next";
import { AIIcon } from "@/icons";
import { utils, writeFile } from "xlsx";

interface deck {
    _id: string;
    title: string;
}

interface Flashcard {
    _id: string;
    title_FC: string;
    type: string;
    createdAt: string;
    deck: deck[];
    flashcard_count: number;
    answer: string;
    image: string;
    sound: string;
}

export default function ListFlashcardInDeck() {
    const { t } = useTranslation();
    const { deck_id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
    const [processedFlashcards, setProcessedFlashcards] = useState<Flashcard[]>([]);
    const [deck, setDeck] = useState<any>(null);

    const fetchFlashcards = useCallback(async (page: number, search?: string) => {
        try {
            setLoading(true);
            setError("");
            const response = await FlashcardService.getFlashcards({
                page: page,
                limit: 10,
                search: search,
                deck_id: deck_id
            });
            setFlashcards(response.data.flashcards || []);
            if (response.data.deck) {
                setDeck(response.data.deck);
            }
            setTotalPages(Math.ceil((response.data.count || 0) / 10));
            setCurrentPage(page);
        } catch (err) {
            setError("Failed to fetch flashcards. Please try again later.");
            console.error("Error fetching flashcards:", err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        }
    }, [deck_id]);

    useEffect(() => {
        const pageFromQuery = Number(searchParams.get("page")) || 1;
        const searchFromQuery = searchParams.get("search") || "";
        setSearchQuery(searchFromQuery);
        fetchFlashcards(pageFromQuery, searchFromQuery);
    }, [fetchFlashcards, searchParams]);

    useEffect(() => {
        const processFlashcards = async () => {
            const updated = await Promise.all(
                flashcards.map(async (fc) => {
                    const image = isValidURL(fc.image) ? fc.image : fc.image ? await fetchSignedUrl(fc.image) : null;
                    const sound = isValidURL(fc.sound) ? fc.sound : fc.sound ? await fetchSignedUrl(fc.sound) : null;
                    return { ...fc, image, sound };
                })
            );
            setProcessedFlashcards(updated);
        };

        processFlashcards();
    }, [flashcards]);

    const handlePageChange = (page: number) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("page", String(page));
        if (searchQuery.trim()) {
            nextParams.set("search", searchQuery.trim());
        } else {
            nextParams.delete("search");
        }
        setSearchParams(nextParams);
    };

    const handleImageUploaded = () => {
        fetchFlashcards(currentPage, searchQuery);
    };

    const isValidURL = (url: string) => /^https?:\/\//i.test(url);

    const fetchSignedUrl = async (fileKey: string): Promise<any> => {
        try {
            const res = await FlashcardService.getFile({ fileKey });
            if (res?.data?.status == 200) {
                return res?.data?.fileUrl;
            }
        } catch (err) {
            console.error("Error fetching signed URL:", err);
            return '';
        }
    };

    const handleExportSample = () => {
        const sampleData = [
            {
                question: "What is the capital of France?",
                answer: "Paris",
                image: "",
                sound: "",
                definition: "The capital and most populous city of France."
            },
            {
                question: "What is 2 + 2?",
                answer: "4",
                image: "",
                sound: "",
                definition: "Basic arithmetic: two plus two equals four."
            }
        ];
        const worksheet = utils.json_to_sheet(sampleData);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, "Flashcards");
        writeFile(workbook, "sample_flashcards.xlsx");
    };

    return (
        <>
            <PageMeta
                title="VIYUME - VIYUME Dashboard"
                description="User Management"
            />
            {deck && deck.title && (
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-white mb-4">
                    <span className="text-gray-500 dark:text-gray-400 font-medium">
                        {t("Deck")}:
                    </span>{" "}
                    <span className="text-brand-600 dark:text-brand-400">
                        {deck.title}
                    </span>
                </h1>
            )}
            <div className="space-y-6">
                <ComponentCard title="">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full">

                        {/* Create Flashcard */}
                        <button
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto"
                            onClick={() => {
                                window.location.href = "/create-flashcard/" + deck_id;
                            }}
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                            {t("Create Flashcard")}
                        </button>

                        {/* Import */}
                        <ImportFlashcardsModal
                            className="w-full sm:w-auto"
                            deck_id={deck_id}
                        />

                        {/* Create With AI */}
                        <button
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors w-full sm:w-auto"
                            onClick={() => {
                                window.location.href = "/create-flashcard-with-ai/" + deck_id;
                            }}
                        >
                            <img
                                src={AIIcon}
                                alt="AI Icon"
                                className="w-5 h-5 object-contain filter invert-[29%] sepia-[98%] saturate-[7500%] hue-rotate-[245deg] brightness-[100%] contrast-[90%]"
                            />
                            {t("Create Flashcard With AI")}
                        </button>

                        {/* Export Sample */}
                        <button
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors w-full sm:w-auto"
                            onClick={handleExportSample}
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                            </svg>
                            {t("Export Sample")}
                        </button>

                    </div>
                    <div className="mb-4 relative">
                        <Input
                            type="text"
                            placeholder="Search flashcards..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onEnter={(value) => {
                                const nextParams = new URLSearchParams(searchParams);
                                nextParams.set("page", "1");
                                if (value.trim()) {
                                    nextParams.set("search", value.trim());
                                } else {
                                    nextParams.delete("search");
                                }
                                setSearchParams(nextParams);
                            }}
                            className="pl-10"
                        />
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                    {loading ? (
                        <div className="animate-pulse">
                            <div className="space-y-4">
                                {[...Array(5)].map((_, index) => (
                                    <div key={index} className="flex items-center space-x-4 py-3">
                                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 text-center p-4">{error}</div>
                    ) : (
                        <FlashcardTable
                            flashcards={processedFlashcards}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            isLoading={loading}
                            onImageUploaded={handleImageUploaded}
                        />
                    )}
                </ComponentCard>
            </div>
        </>
    );
}
